import "./rate-confirmation-modal.css";

import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import type { SwitchChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Switch } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	dataLine,
	download_01,
	eye,
	link_01,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useUpdateAllCarrierMargins } from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";

import {
	downloadPdf,
	openPdfInNewTab,
} from "../../../shipment-details/components/tender-modal/utils/pdf-utils.ts";
import { useRateConfirmationGenerate } from "../carrier-card/hooks/use-rate-confirmation-generate.ts";
import { CompressedCarrierCards } from "../shared/compressed-carrier-cards.tsx";

type ModalProps = {
	showModal: boolean;
	onToggleModal: (name: string) => void;
	carriers: CarrierPrice[];
};

export const RateConfirmationModal = ({
	showModal,
	onToggleModal,
	carriers,
}: ModalProps) => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { isReady, isEnabled } = useFeatureFlagGate("traxLtlRateConV2");
	const name = "confirmationRate";
	// const emailSchema = z.string().email();

	// const [emailError, setEmailError] = useState<string>("");

	const [selectedNames, setSelectedNames] = useState<string[]>([]);
	const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
	const [selectedCarrierIds, setSelectedCarrierIds] = useState<number[]>([]);
	// const [email, setEmail] = useState<string>("");
	const [generatedDoc, setGeneratedDoc] = useState<ArrayBuffer | undefined>(
		undefined,
	);
	const { showSuccess } = useNotificationActions();

	const {
		generateRateConfirmation,
		isLoading,
		isSuccess,
		data,
		generateViewUrl,
	} = useRateConfirmationGenerate();

	const { updateAllCarrierMargins, isPending: isUpdatingMargins } =
		useUpdateAllCarrierMargins();

	const handleCopyLink = useCallback(() => {
		const url = generateViewUrl(selectedCarrierIds.map(String)).fullUrl;
		void navigator.clipboard.writeText(url);
		showSuccess("Link has been copied to your clipboard!");
	}, [generateViewUrl, selectedCarrierIds, showSuccess]);

	useAutoSpinner(isUpdatingMargins || isLoading);

	// const uniqueCarriers = useMemo(() => {
	// 	const set = new Set<string>();
	// 	for (const carrier of carriers) set.add(carrier.carrier.name);
	// 	return [...set];
	// }, [carriers]);

	const selectedNamesLower = useMemo(
		() => selectedNames.map((name) => name.toLowerCase()),
		[selectedNames],
	);

	const filteredCarriers = useMemo(() => {
		if (selectedNamesLower.length === 0) return carriers;
		const nameSet = new Set(selectedNamesLower);
		return carriers.filter((carrier) =>
			nameSet.has(carrier.carrier.name.toLowerCase()),
		);
	}, [carriers, selectedNamesLower]);

	useEffect(() => {
		setSelectedCarrierIds([]);
		setIsAllSelected(false);
		setGeneratedDoc(undefined);
	}, [selectedNames]);

	useEffect(() => {
		setGeneratedDoc(undefined);
	}, [isAllSelected, selectedCarrierIds]);

	useEffect(() => {
		if (isSuccess && data) {
			setGeneratedDoc(data);
		}
	}, [isSuccess, data]);

	const handleSelectAll = (isAllChecked: boolean) => {
		setIsAllSelected(isAllChecked);
		if (isAllChecked) {
			setSelectedCarrierIds(filteredCarriers.map((c) => c.id));
		} else {
			setSelectedCarrierIds([]);
		}
	};

	const onCarrierSelection = (carrier: CarrierPrice) => {
		setSelectedCarrierIds((prev) => {
			let newSelection;
			if (prev.includes(carrier.id)) {
				if (isAllSelected) {
					setIsAllSelected(false);
				}
				newSelection = prev.filter((id) => id !== carrier.id);
			} else {
				newSelection = [...prev, carrier.id];
			}
			if (newSelection.length === filteredCarriers.length) {
				setIsAllSelected(true);
			}
			return newSelection;
		});
	};

	// const onEmailCustomer = () => {
	// 	if (emailSchema.safeParse(email).success) {
	// 		// Email is valid, proceed with sending
	// 		console.log("Sending email to:", email);
	// 		setEmailError("");
	// 	} else {
	// 		// Email is invalid, show error
	// 		console.error("Invalid email address:", email);
	// 		setEmailError("Invalid email address");
	// 	}
	// };

	const resetAllState = () => {
		// setEmailError("");
		setSelectedNames([]);
		setSelectedCarrierIds([]);
		setIsAllSelected(false);
		// setEmail("");
		setGeneratedDoc(undefined);
	};

	const handleGenerateRateConfirmation = async () => {
		try {
			//Save carrier margins first
			const updatedETag = await updateAllCarrierMargins(
				quoteState.id,
				quoteState.eTag,
				quoteState.carrierPrices,
			);
			if (!updatedETag) {
				console.error(
					"Failed to save carrier margins before generating rate confirmation.",
				);
				return;
			}
			generateRateConfirmation(selectedCarrierIds.map(String));
		} catch (error) {
			console.error("Error generating rate confirmation:", error);
		}
	};
	if (!showModal) return null;

	if (!isReady) return <Loader />;

	return (
		<TraxModal
			className={"rate-confirmation-modal"}
			onClose={() => {
				onToggleModal(name);
				resetAllState();
			}}
			title={"Rate Confirmation"}
			width={"50%"}
		>
			<div className={"form-row"}>
				<div className={"form-group"}>
					<Label className={"select-all-label"}>Select All</Label>
					<Switch
						checked={isAllSelected}
						className={"select-all-switch"}
						onChange={(e: SwitchChangeEvent) => handleSelectAll(e.value)}
					/>
				</div>
				{/* <div className={"form-group filter-group"}>
					<Label className={"filter-carriers-label"}>Filter Carriers</Label>
					<MultiSelect
						data={uniqueCarriers}
						onChange={(event: MultiSelectChangeEvent) =>
							setSelectedNames(event.target.value as string[])
						}
					/>
				</div> */}
			</div>
			<div className={"rate-confirmation-carriers-wrapper"}>
				<div className={"rate-confirmation-carriers"}>
					{/* {filteredCarriers.map((carrier) => {
						const isCarrierSelected = selectedCarrierIds.includes(carrier.id);
						return (
							<RateConfirmationCarrier
								key={carrier.id}
								carrier={carrier}
								isCarrierSelected={isCarrierSelected}
								onCarrierSelection={onCarrierSelection}
							/>
						);
					})} */}
					<CompressedCarrierCards
						carriers={filteredCarriers}
						onCarrierSelection={onCarrierSelection}
						selectedCarrierIds={selectedCarrierIds}
					/>
				</div>
			</div>
			<DialogActionsBar>
				<div className={"action-container"}>
					<div className={"top-row"}>
						<div className={"customer-section"}>
							{/* <div className={"customer-name-row"}>
								<Label>Customer Name:</Label>
								<span className={"required"}> *</span>
								<Input
									className={"customer-email-input"}
									disabled={true}
									name={"email"}
									onChange={(e: InputChangeEvent) =>
										setEmail(e.target.value as string)
									}
									type={"email"}
									valid={emailError === ""}
								/>
								<Button disabled={email.length === 0} onClick={onEmailCustomer}>
									Email Customer
								</Button>
							</div> */}
						</div>
						<div className={"action-button-group"}>
							<Button
								disabled={
									selectedCarrierIds.length === 0 ||
									isLoading ||
									isUpdatingMargins
								}
								id={"carrier-button"}
								onClick={() => {
									handleGenerateRateConfirmation();
								}}
								svgIcon={dataLine}
							>
								Generate
							</Button>
							<Button
								disabled={!generatedDoc}
								id={"carrier-button"}
								onClick={() => {
									if (!generatedDoc) return;
									if (isEnabled) {
										window.open(
											generateViewUrl(selectedCarrierIds.map(String)).fullUrl,
											"_blank",
										);
									} else {
										openPdfInNewTab(generatedDoc, "rate-confirmation.pdf");
									}
								}}
								svgIcon={eye}
							>
								View
							</Button>
							<Button
								disabled={!generatedDoc}
								id={"carrier-button"}
								onClick={() => {
									if (!generatedDoc) return;
									downloadPdf(generatedDoc, "rate-confirmation.pdf");
								}}
								svgIcon={download_01}
							>
								Download
							</Button>
							<Button
								disabled={!generatedDoc}
								id={"carrier-button"}
								onClick={handleCopyLink}
								svgIcon={link_01}
							>
								Copy Link
							</Button>
							{/* <Button
								disabled={!generatedDoc}
								id={"carrier-button"}
								svgIcon={copy_01}
							>
								Copy Selected Rates
							</Button> */}
							<Button
								onClick={() => {
									onToggleModal(name);
									resetAllState();
								}}
							>
								Close
							</Button>
						</div>
					</div>
				</div>
			</DialogActionsBar>
		</TraxModal>
	);
};
